import React, { useEffect, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * DefiniteMorph — "A Book, The Book" indefinite→definite transformer.
 *
 * Each row shows a bare (indefinite) word with a pulsing "+ الـ" button;
 * tapping it reveals the definite form with the الـ prefix highlighted, and
 * plays it. Completes when every pair has been transformed.
 */
function DefWord({ defAr, highlightEnding }) {
  // Color the الـ prefix (plus the sukūn riding on the ل, when present) —
  // and, when the lesson is about the endings themselves, the final short
  // vowel together with its carrier letter. Lesson 25 morphs whole idafa
  // chains (بَابُ بَيْتٍ → بَابُ الْبَيْتِ), where both the الـ and the
  // ending belong to the LAST word; for a one-word pair that word is the
  // whole string, so single-word lessons render exactly as before.
  const cut = defAr.lastIndexOf(" ");
  const head = cut === -1 ? "" : defAr.slice(0, cut + 1);
  const word = cut === -1 ? defAr : defAr.slice(cut + 1);
  const m = word.match(/^الْ?/);
  if (!m) return defAr;
  const rest = word.slice(m[0].length);
  // Damma / fatha / kasra on the last letter.
  const end = highlightEnding ? rest.match(/.[َُِ]$/u) : null;
  return (
    <>
      {head}
      <span style={{ color: "var(--tag-violet)" }}>{m[0]}</span>
      {end ? (
        <>
          {rest.slice(0, -end[0].length)}
          <span style={{ color: "var(--tag-violet)" }}>{end[0]}</span>
        </>
      ) : (
        rest
      )}
    </>
  );
}

function IndefWord({ indefAr }) {
  // Color the trailing tanwīn — the doubled end-mark the takeaway names —
  // together with its carrier letter (and the silent seat alif riding after
  // a fathatan, as in كِتَابًا): browsers don't reliably paint a bare
  // combining mark in its own color inside a shaped Arabic run.
  const m = indefAr.match(/.[ًٌٍ]ا?$/);
  if (!m) return indefAr;
  return (
    <>
      {indefAr.slice(0, -m[0].length)}
      <span style={{ color: "var(--tag-violet)" }}>{m[0]}</span>
    </>
  );
}

export default function DefiniteMorph({ data, onComplete, audio }) {
  const [morphedIds, setMorphedIds] = useState(() => new Set());

  const pairs = data.pairs;
  const unitLabel = data.unit_label || "words";
  const allMorphed = morphedIds.size >= pairs.length;

  useEffect(() => {
    if (allMorphed) onComplete();
  }, [allMorphed, onComplete]);

  const handleMorph = (pair) => {
    if (morphedIds.has(pair.id)) return;
    setMorphedIds((prev) => new Set(prev).add(pair.id));
    audio.play(pair.def_ar);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-4">
        <ProgressPill>{morphedIds.size}/{pairs.length} {unitLabel} transformed</ProgressPill>
      </div>

      <div className="flex flex-col gap-3">
        {pairs.map((pair) => {
          const morphed = morphedIds.has(pair.id);
          return (
            <div
              key={pair.id}
              className="rounded-xl px-4 py-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3"
              style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}
            >
              <div className="text-center">
                <p dir="rtl" lang="ar" className="font-arabic text-3xl leading-[1.8] m-0" style={{ color: "var(--parchment)" }}>
                  <IndefWord indefAr={pair.indef_ar} />
                </p>
                <p className="text-xs mt-1 mb-0 inline-flex items-center gap-2" style={{ color: "var(--parchment-dim)" }}>
                  {pair.indef_en}
                  {audio.audioAllowed && (
                    <PlayButton text={pair.indef_ar} playingText={audio.playingText} onPlay={audio.play} />
                  )}
                </p>
              </div>

              <div className="flex justify-center">
                {morphed ? (
                  <span className="text-xl" aria-hidden="true" style={{ color: "var(--gold-dim)" }}>→</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleMorph(pair)}
                    className="rounded-lg px-3 pt-4 pb-1.5 font-arabic text-2xl leading-[1.4] transition-all hover:brightness-125 animate-pulse font-semibold"
                    style={{
                      color: "var(--gold-bright)",
                      background: "var(--gold-bg)",
                      border: "1px dashed var(--gold-dim)",
                    }}
                    title='Tap to add the الـ and make it "the"'
                  >
                    {data.button_label || "+ الـ"}
                  </button>
                )}
              </div>

              <div className="text-center">
                {morphed ? (
                  <>
                    <p dir="rtl" lang="ar" className="font-arabic text-3xl leading-[1.8] m-0" style={{ color: "var(--parchment)" }}>
                      <DefWord defAr={pair.def_ar} highlightEnding={!!data.highlight_endings} />
                    </p>
                    <p className="text-xs mt-1 mb-0 inline-flex items-center gap-2" style={{ color: "var(--parchment-dim)" }}>
                      {pair.def_en}
                      {audio.audioAllowed && (
                        <PlayButton text={pair.def_ar} playingText={audio.playingText} onPlay={audio.play} />
                      )}
                    </p>
                  </>
                ) : (
                  <p className="font-arabic text-3xl m-0" aria-hidden="true" style={{ color: "var(--parchment-dim)", opacity: 0.35 }}>
                    ؟
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {allMorphed && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">✨</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
